/**
 * Resolves the user's "active car" on the client by combining `useCars()`
 * with `useAuth().me.last_active_car_id`.
 *
 * Mirrors the backend's `resolve_active_car` fallback chain:
 *   1. `users.last_active_car_id` if it exists in the cars list
 *   2. otherwise the first accessible car
 *   3. otherwise `null` (caller renders an empty-state CTA)
 *
 * Use this in the car-switcher and any UI that needs to display the
 * currently-selected car. Reads are stale-tolerant — invalidation is
 * driven by `useSetActiveCar` (which invalidates `["auth","me"]`).
 */

import { useMemo } from "react";

import { useCars } from "../api/cars";
import { useAuth } from "./AuthProvider";
import type { CarSummary } from "../api/types";

export interface ActiveCarState {
  cars: CarSummary[];
  activeCar: CarSummary | null;
  isLoading: boolean;
  hasCars: boolean;
}

export function useActiveCar(): ActiveCarState {
  const { me, isLoading: authLoading } = useAuth();
  const carsQuery = useCars();

  return useMemo<ActiveCarState>(() => {
    const cars = carsQuery.data?.cars ?? [];
    const lastActive = me?.last_active_car_id ?? null;
    const active =
      cars.find((c) => c.id === lastActive) ?? cars[0] ?? null;
    return {
      cars,
      activeCar: active,
      isLoading: authLoading || carsQuery.isLoading,
      hasCars: cars.length > 0,
    };
  }, [me, authLoading, carsQuery.data, carsQuery.isLoading]);
}
